import { useState } from "react";
import type { ReactNode } from "react";
import { ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";
import { getPipelineDetail, getPipelines } from "../api/client.js";
import type {
  ChangeCellState,
  CheckState,
  PipelineApp,
  PipelineChange,
  PipelineDetailResponse,
  PipelineKind,
  PipelineLane,
  PipelineLaneDetail,
  PipelinesResponse,
  PipelineStage,
  StageState,
} from "../api/types.js";
import { useApi } from "../lib/useApi.js";
import { Card, EmptyState, Pill, ProgressBar, StatusDot } from "../components/ui.js";
import type { Tone } from "../components/ui.js";
import { ErrorState, LoadingState, SectionErrors } from "../components/common.js";

const PAGE_STYLES = `
@keyframes ops-pipe-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
.pipe-lane-row:hover { background: var(--bg-subtle); }
.pipe-change-row:hover { background: var(--bg-subtle); }
`;

// Lane icons in the Shell.tsx house style: 24 viewBox, stroke 1.75, currentColor.
function laneIcon(paths: ReactNode) {
  return function Icon() {
    return (
      <svg
        width={15}
        height={15}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flex: "none" }}
        aria-hidden="true"
      >
        {paths}
      </svg>
    );
  };
}

const RocketIcon = laneIcon(
  <>
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </>,
);

const FlagsIcon = laneIcon(
  <>
    <rect width="20" height="12" x="2" y="6" rx="6" ry="6" />
    <circle cx="16" cy="12" r="2" />
  </>,
);

const LayersIcon = laneIcon(
  <>
    <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
    <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12" />
    <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
  </>,
);

const LANES = [
  { key: "release" as const, label: "Release", Icon: RocketIcon },
  { key: "flags" as const, label: "Flags & A/B", Icon: FlagsIcon },
  { key: "infra" as const, label: "Infra", Icon: LayersIcon },
];

type LaneMeta = (typeof LANES)[number];

const KIND_LABEL: Record<PipelineKind, string> = {
  release: "Release",
  flags: "Flags & A/B",
  infra: "Infra",
};

interface StatusView {
  label: string;
  tone: Tone;
  pulse: boolean;
  // Dead lanes (nothing to open) get a faded chevron so the row reads honestly.
  dim: boolean;
}

const STATUS_VIEW: Record<PipelineLane["status"], StatusView> = {
  healthy: { label: "HEALTHY", tone: "ok", pulse: false, dim: false },
  deploying: { label: "DEPLOYING", tone: "info", pulse: true, dim: false },
  blocked: { label: "BLOCKED", tone: "alarm", pulse: false, dim: false },
  failed: { label: "FAILED", tone: "alarm", pulse: false, dim: false },
  idle: { label: "IDLE", tone: "nodata", pulse: false, dim: false },
  "not-set-up": { label: "NOT SET UP", tone: "nodata", pulse: false, dim: true },
  "not-configured": { label: "NOT CONFIGURED", tone: "nodata", pulse: false, dim: true },
  unknown: { label: "UNKNOWN", tone: "nodata", pulse: false, dim: false },
};

const STAGE_TONE: Record<StageState, Tone> = {
  ok: "ok",
  passing: "ok",
  deploying: "info",
  stable: "ok",
  blocked: "alarm",
  idle: "nodata",
  none: "nodata",
  warn: "warn",
};

const STAGE_LABEL: Record<StageState, string> = {
  ok: "OK",
  passing: "PASSING",
  deploying: "DEPLOYING",
  stable: "STABLE",
  blocked: "BLOCKED",
  idle: "IDLE",
  none: "N/A",
  warn: "REVIEW",
};

const CHECK_TONE: Record<CheckState, Tone> = {
  passed: "ok",
  failed: "alarm",
  pending: "warn",
  info: "nodata",
};

const CELL_TONE: Record<ChangeCellState, Tone> = {
  passed: "ok",
  deployed: "ok",
  live: "ok",
  deploying: "info",
  open: "info",
  waiting: "nodata",
  none: "nodata",
};

// Vertical accent for the app dot (design uses per-app accent; API only gives the
// vertical string, so derive deterministically from it).
const VERTICAL_PALETTE = ["#0CA678", "#7C5CFC", "#12A8C9", "#F08C00", "#1668FF"];

function verticalColor(vertical: string): string {
  const v = vertical.toLowerCase();
  if (/health|sant|clinic|m[eé]dec|api|core/.test(v)) return "#0CA678";
  if (/educ|school|learn/.test(v)) return "#7C5CFC";
  if (/connect|network|bridge|market|site/.test(v)) return "#12A8C9";
  if (/ops|dashboard|console|admin/.test(v)) return "#F08C00";
  if (/terra|infra|tofu/.test(v)) return "#1668FF";
  let hash = 0;
  for (let i = 0; i < vertical.length; i += 1) hash = (hash * 31 + vertical.charCodeAt(i)) >>> 0;
  return VERTICAL_PALETTE[hash % VERTICAL_PALETTE.length];
}

function PulsingDot({ pulse }: { pulse: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 5,
        height: 5,
        borderRadius: 999,
        background: "currentColor",
        animation: pulse ? "ops-pipe-pulse 1.6s ease-in-out infinite" : undefined,
      }}
    />
  );
}

// ---- list view ----

function LaneRowContent({ lane, meta }: { lane: PipelineLane; meta: LaneMeta }) {
  const view = STATUS_VIEW[lane.status];
  const Icon = meta.Icon;
  return (
    <>
      <span
        style={{
          width: 28,
          height: 28,
          flex: "none",
          borderRadius: 8,
          background: "var(--bg-subtle)",
          color: "var(--fg-subtle)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon />
      </span>
      <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--fg)" }}>{meta.label}</span>
          {lane.badge && (
            <span
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                padding: "1px 6px",
                borderRadius: 999,
                background: "var(--accent-bg)",
                color: "var(--accent)",
                whiteSpace: "nowrap",
                flex: "none",
              }}
            >
              {lane.badge}
            </span>
          )}
        </span>
        <span
          style={{
            fontSize: 11,
            color: "var(--fg-subtle)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {lane.detail}
        </span>
      </span>
      <span style={{ flex: "none" }}>
        <Pill tone={view.tone}>
          <PulsingDot pulse={view.pulse} />
          {view.label}
        </Pill>
      </span>
      <ChevronRight
        size={15}
        aria-hidden="true"
        style={{ color: "var(--fg-faint)", flex: "none", opacity: view.dim ? 0.25 : 1 }}
      />
    </>
  );
}

function LaneRow({ lane, meta, onOpen }: { lane: PipelineLane; meta: LaneMeta; onOpen: () => void }) {
  const interactive = !STATUS_VIEW[lane.status].dim;
  const base = {
    display: "flex",
    alignItems: "center",
    gap: 11,
    padding: "11px 16px",
    borderTop: "1px solid var(--divider)",
    width: "100%",
  } as const;

  if (!interactive) {
    return (
      <div style={base}>
        <LaneRowContent lane={lane} meta={meta} />
      </div>
    );
  }
  return (
    <button
      type="button"
      className="pipe-lane-row"
      onClick={onOpen}
      style={{ ...base, background: "transparent", border: "none", borderTop: "1px solid var(--divider)", cursor: "pointer", textAlign: "left", font: "inherit", color: "inherit" }}
    >
      <LaneRowContent lane={lane} meta={meta} />
    </button>
  );
}

function AppCard({ app, onOpenLane }: { app: PipelineApp; onOpenLane: (kind: PipelineKind) => void }) {
  return (
    <Card flush style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px" }}>
        <span style={{ width: 10, height: 10, flex: "none", borderRadius: 3, background: verticalColor(app.vertical) }} />
        <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
          <span className="display" style={{ fontSize: 14.5 }}>
            {app.name}
          </span>
          <span
            style={{
              fontSize: 11,
              color: "var(--fg-subtle)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {app.vertical} · {app.region}
          </span>
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            padding: "2px 8px",
            borderRadius: 999,
            background: "var(--tone-nodata-bg)",
            color: "var(--tone-nodata-fg)",
            whiteSpace: "nowrap",
            flex: "none",
          }}
        >
          {app.env}
        </span>
      </div>
      {LANES.map((meta) => (
        <LaneRow key={meta.key} lane={app.lanes[meta.key]} meta={meta} onOpen={() => onOpenLane(meta.key)} />
      ))}
    </Card>
  );
}

// ---- detail view ----

function StageNode({ stage }: { stage: PipelineStage }) {
  const tone = STAGE_TONE[stage.state];
  return (
    <div
      style={{
        flex: 1,
        minWidth: 190,
        display: "flex",
        flexDirection: "column",
        gap: 7,
        padding: "13px 15px",
        border: "1px solid var(--border)",
        borderRadius: 14,
        background: "var(--surface)",
        boxShadow: "var(--shadow-xs)",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--fg-faint)" }}>
          {stage.label}
        </span>
        <Pill tone={tone}>
          <PulsingDot pulse={stage.state === "deploying"} />
          {STAGE_LABEL[stage.state]}
        </Pill>
      </span>
      <span className="mono" style={{ fontSize: 13, fontWeight: 500, color: "var(--fg)" }}>
        {stage.version ?? "—"}
      </span>
      {stage.sub && <span style={{ fontSize: 11, lineHeight: 1.5, color: "var(--fg-subtle)" }}>{stage.sub}</span>}
      {stage.progressPct != null && <ProgressBar pct={stage.progressPct} tone={tone} />}
      {stage.foot && (
        <span style={{ marginTop: "auto", borderTop: "1px solid var(--divider)", paddingTop: 7, fontSize: 10.5, color: "var(--fg-faint)" }}>
          {stage.foot}
        </span>
      )}
    </div>
  );
}

function StageFlow({ lane }: { lane: PipelineLaneDetail }) {
  return (
    <Card style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <span className="display" style={{ fontSize: 14.5 }}>
          {lane.title}
        </span>
        {lane.flow && <span style={{ fontSize: 11.5, color: "var(--fg-faint)" }}>{lane.flow}</span>}
      </div>
      <div style={{ overflowX: "auto" }}>
        <div style={{ display: "flex", alignItems: "stretch", gap: 0, minWidth: lane.stages.length * 210 }}>
          {lane.stages.map((stage, i) => (
            <div key={stage.key} style={{ display: "flex", alignItems: "stretch", flex: 1, minWidth: 0 }}>
              {i > 0 && (
                <div style={{ flex: "none", width: 30, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--fg-faint)" }}>
                  <ArrowRight size={15} aria-hidden="true" />
                </div>
              )}
              <StageNode stage={stage} />
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function GateChecks({ lane }: { lane: PipelineLaneDetail }) {
  if (lane.checks.length === 0) return null;
  return (
    <Card style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <span className="display" style={{ fontSize: 14.5 }}>
        Gate checks
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {lane.checks.map((check) => (
          <div key={check.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <StatusDot tone={CHECK_TONE[check.state]} size={8} />
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: "var(--fg)" }}>{check.label}</span>
            <span className="mono" style={{ fontSize: 11.5, color: "var(--fg-subtle)" }}>{check.detail}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ChangeCells({ change, columns }: { change: PipelineChange; columns: string[] }) {
  return (
    <>
      <span style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 9 }}>
        <span className="mono" style={{ fontSize: 11.5, color: "var(--accent)", flex: "none" }}>{change.sha}</span>
        <span style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--fg)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {change.message || "(no message)"}
          </span>
          <span style={{ fontSize: 10.5, color: "var(--fg-subtle)" }}>
            {change.author}
            {change.when ? ` · ${new Date(change.when).toLocaleString()}` : ""}
          </span>
        </span>
      </span>
      {columns.map((col, i) => {
        const cell = change.cells[i];
        return (
          <span key={col} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, fontWeight: 700, color: "var(--fg-muted)" }}>
            {cell ? (
              <>
                <StatusDot tone={CELL_TONE[cell.state]} size={6} />
                {cell.label}
              </>
            ) : (
              <span style={{ color: "var(--fg-faint)" }}>—</span>
            )}
          </span>
        );
      })}
    </>
  );
}

function ChangesTable({ lane }: { lane: PipelineLaneDetail }) {
  if (lane.changeColumns.length === 0) return null;
  const grid = `minmax(0, 2.4fr) ${lane.changeColumns.map(() => "120px").join(" ")}`;
  return (
    <Card flush>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, padding: "14px 20px 10px" }}>
        <span className="display" style={{ fontSize: 14.5 }}>
          Changes
        </span>
        {lane.followNote && <span style={{ fontSize: 11.5, color: "var(--fg-faint)" }}>{lane.followNote}</span>}
      </div>
      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: 720 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: grid,
              gap: "0 12px",
              padding: "7px 20px",
              borderTop: "1px solid var(--divider)",
              borderBottom: "1px solid var(--divider)",
              background: "var(--bg-subtle)",
            }}
          >
            <span className="label--table">Change</span>
            {lane.changeColumns.map((col) => (
              <span key={col} className="label--table">{col}</span>
            ))}
          </div>
          {lane.changes.length === 0 ? (
            <div style={{ padding: "16px 20px", fontSize: 12, color: "var(--fg-subtle)" }}>No recent changes.</div>
          ) : (
            lane.changes.map((change, idx) => (
              <div
                key={`${change.sha}-${idx}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: grid,
                  gap: "0 12px",
                  alignItems: "center",
                  padding: "9px 20px",
                  borderBottom: "1px solid var(--divider)",
                }}
              >
                <ChangeCells change={change} columns={lane.changeColumns} />
              </div>
            ))
          )}
        </div>
      </div>
    </Card>
  );
}

function PromoteControl({ lane }: { lane: PipelineLaneDetail }) {
  if (!lane.promote) return null;
  return (
    <Card style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <button
        type="button"
        disabled
        style={{
          alignSelf: "flex-start",
          padding: "9px 16px",
          borderRadius: 9,
          border: "1px solid var(--border)",
          background: "var(--bg-subtle)",
          color: "var(--fg-faint)",
          fontFamily: "var(--font-body)",
          fontSize: 12.5,
          fontWeight: 700,
          cursor: "not-allowed",
          opacity: 0.7,
        }}
      >
        {lane.promote.label}
      </button>
      <span style={{ fontSize: 11.5, color: "var(--fg-subtle)" }}>{lane.promote.note}</span>
    </Card>
  );
}

function LaneBody({ lane }: { lane: PipelineLaneDetail }) {
  if (!lane.available) {
    return (
      <Card>
        <EmptyState title="Nothing to show here" message={lane.emptyNote ?? "This pipeline is not set up."} tone="nodata" />
      </Card>
    );
  }
  return (
    <>
      <StageFlow lane={lane} />
      <GateChecks lane={lane} />
      <ChangesTable lane={lane} />
      <PromoteControl lane={lane} />
    </>
  );
}

function KindSwitcher({ current, onKind }: { current: PipelineKind; onKind: (kind: PipelineKind) => void }) {
  return (
    <div style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 2, padding: 3, borderRadius: 9, background: "var(--surface)", border: "1px solid var(--border)" }}>
      {LANES.map((meta) => {
        const active = current === meta.key;
        const Icon = meta.Icon;
        return (
          <button
            key={meta.key}
            type="button"
            onClick={() => onKind(meta.key)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 7,
              border: "none",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontSize: 12,
              fontWeight: 600,
              background: active ? "var(--accent-bg)" : "transparent",
              color: active ? "var(--accent)" : "var(--fg-muted)",
            }}
          >
            <Icon />
            {meta.label}
          </button>
        );
      })}
    </div>
  );
}

interface DetailProps {
  appKey: string;
  kind: PipelineKind;
  onBack: () => void;
  onKind: (kind: PipelineKind) => void;
  refreshKey: number;
}

function PipelineDetail({ appKey, kind, onBack, onKind, refreshKey }: DetailProps) {
  const { data, loading, error } = useApi<PipelineDetailResponse>(
    () => getPipelineDetail(appKey),
    [appKey],
    refreshKey,
  );

  const backPill = (
    <button
      type="button"
      onClick={onBack}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        borderRadius: 999,
        border: "1px solid var(--border)",
        background: "var(--surface)",
        color: "var(--fg-muted)",
        fontFamily: "var(--font-body)",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      <ArrowLeft size={14} aria-hidden="true" />
      Pipelines
    </button>
  );

  if (loading && !data) {
    return (
      <>
        {backPill}
        <Card>
          <LoadingState label="Loading pipeline" />
        </Card>
      </>
    );
  }
  if (error && !data) {
    return (
      <>
        {backPill}
        <Card>
          <ErrorState message={error} />
        </Card>
      </>
    );
  }
  if (!data) return backPill;

  const lane = data.lanes[kind];
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {backPill}
        <span style={{ color: "var(--fg-faint)" }}>/</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--fg)" }}>{data.app.name}</span>
        <span style={{ color: "var(--fg-faint)" }}>/</span>
        <span style={{ fontSize: 13, color: "var(--fg-muted)" }}>{KIND_LABEL[kind]}</span>
        <KindSwitcher current={kind} onKind={onKind} />
      </div>
      <LaneBody lane={lane} />
      {data.errors.length > 0 && <SectionErrors errors={data.errors} />}
    </>
  );
}

// ---- page ----

interface View {
  mode: "list" | "detail";
  appKey: string;
  kind: PipelineKind;
}

export function PipelinesPage({ refreshKey }: { refreshKey: number }) {
  const { data, loading, error } = useApi<PipelinesResponse>(getPipelines, [], refreshKey);
  const [view, setView] = useState<View>({ mode: "list", appKey: "", kind: "release" });

  if (loading && !data) {
    return (
      <Card>
        <LoadingState label="Loading pipelines" />
      </Card>
    );
  }
  if (error && !data) {
    return (
      <Card>
        <ErrorState message={error} />
      </Card>
    );
  }
  if (!data) {
    return (
      <Card>
        <EmptyState title="No pipeline data" message="The pipelines endpoint returned nothing." tone="nodata" />
      </Card>
    );
  }

  if (view.mode === "detail") {
    return (
      <>
        <style>{PAGE_STYLES}</style>
        <PipelineDetail
          appKey={view.appKey}
          kind={view.kind}
          refreshKey={refreshKey}
          onBack={() => setView({ mode: "list", appKey: "", kind: "release" })}
          onKind={(kind) => setView((v) => ({ ...v, kind }))}
        />
      </>
    );
  }

  const { apps } = data;
  return (
    <>
      <style>{PAGE_STYLES}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "var(--fg-subtle)" }}>
          {apps.length} apps · release, flags &amp; A/B, and infra pipelines each — open one to drill in
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--fg-faint)" }}>read-only</span>
      </div>

      {apps.length === 0 ? (
        <Card>
          <EmptyState title="No apps" message="No pipeline apps are configured." tone="nodata" />
        </Card>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 16 }}>
          {apps.map((app) => (
            <AppCard
              key={app.key}
              app={app}
              onOpenLane={(kind) => setView({ mode: "detail", appKey: app.key, kind })}
            />
          ))}
        </div>
      )}
    </>
  );
}
